import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '@shared/redux/root-state';
import type { KnowledgeFileUploadKnowledgeFileUploadPostApiArg } from '@shared/api';

const getFileNameFromDisposition = (disposition?: string | null) => {
  if (!disposition) return undefined;

  const fileNameStarMatch = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (fileNameStarMatch?.[1]) {
    try {
      return decodeURIComponent(fileNameStarMatch[1]);
    } catch {
      return fileNameStarMatch[1];
    }
  }

  const fileNameMatch = disposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
  return fileNameMatch?.[2];
};

export const apiBlob = createApi({
  reducerPath: 'apiBlob',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers, { getState }) => {
      const { access_token } = (getState() as RootState).auth.authInfo || {};
      const language = (getState() as RootState).appSettings.language || 'en';
      if (access_token) {
        headers.set('Authorization', `Bearer ${access_token}`);
      }
      headers.set('X-Language', language);
      return headers;
    },
    responseHandler: async (response) => {
      const contentType = response.headers.get('Content-Type') || '';
      if (!contentType.includes('application/json')) {
        return await response.blob();
      }
      return await response.json();
    },
  }),
  endpoints: (builder) => ({
    getKnowledgeFile: builder.query<Blob, { knowledge_file_id: number }>({
      query: ({ knowledge_file_id }) => ({
        url: `/knowledge/file/download/`,
        method: 'GET',
        params: { knowledge_base_id: knowledge_file_id },
      }),
    }),

    uploadKnowledgeFile: builder.mutation<Blob, KnowledgeFileUploadKnowledgeFileUploadPostApiArg>({
      query: (queryArg) => ({
        url: `/knowledge/file/upload`,
        method: 'POST',
        body: queryArg.bodyKnowledgeFileUploadKnowledgeFileUploadPost,
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }),
    }),

    downloadKnowledgeBaseFile: builder.query<
      { blob: Blob; fileName?: string },
      { knowledge_base_id: number }
    >({
      query: ({ knowledge_base_id }) => ({
        url: `/knowledge/file/download`,
        method: 'GET',
        params: { knowledge_base_id },
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }),
      transformResponse: (response: Blob, meta) => ({
        blob: response,
        fileName: getFileNameFromDisposition(meta?.response?.headers.get('content-disposition')),
      }),
    }),
  }),
});

export const {
  useGetKnowledgeFileQuery,
  useUploadKnowledgeFileMutation,
  useDownloadKnowledgeBaseFileQuery,
  useLazyDownloadKnowledgeBaseFileQuery,
} = apiBlob;
